/**
 * Format a number as currency
 */
export function formatCurrency(
  amount: number,
  currency = 'USD',
  locale = 'en-US',
  fractionDigits?: number
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    ...(fractionDigits !== undefined && {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }),
  }).format(amount);
}

/**
 * Format a number as a compact currency (e.g., $1.2K)
 */
export function formatCompactCurrency(
  amount: number,
  currency = 'USD',
  locale = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amount);
}

/**
 * Format a percentage
 */
export function formatPercent(value: number, decimals = 0): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format a date
 */
export function formatDate(
  date: string | Date,
  options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  },
  locale = 'en-US'
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, options).format(d);
}

/**
 * Format a date as short relative time (e.g., "2d ago", "15m ago").
 * Compact form so labels fit comfortably on narrow viewports.
 * `locale` is accepted for API stability but unused — output is locale-neutral.
 */
export function formatRelativeTime(date: string | Date, _locale = 'en-US'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const diffMs = Date.now() - d.getTime();
  const future = diffMs < 0;
  const absMs = Math.abs(diffMs);
  const sec = Math.floor(absMs / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);

  let unit: string;
  let n: number;
  if (sec < 60) {
    unit = 's';
    n = sec;
  } else if (min < 60) {
    unit = 'm';
    n = min;
  } else if (hr < 24) {
    unit = 'h';
    n = hr;
  } else if (day < 7) {
    unit = 'd';
    n = day;
  } else if (day < 30) {
    unit = 'w';
    n = Math.floor(day / 7);
  } else if (day < 365) {
    unit = 'mo';
    n = Math.floor(day / 30);
  } else {
    unit = 'y';
    n = Math.floor(day / 365);
  }

  return future ? `in ${n}${unit}` : `${n}${unit} ago`;
}

/**
 * Get start and end dates for a period
 */
export function getDateRange(period: 'week' | 'month' | 'year' | 'ytd'): {
  startDate: Date;
  endDate: Date;
} {
  const now = new Date();
  const endDate = new Date(now);
  let startDate: Date;

  switch (period) {
    case 'week':
      startDate = new Date(now);
      startDate.setDate(now.getDate() - 7);
      break;
    case 'month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'year':
      startDate = new Date(now);
      startDate.setFullYear(now.getFullYear() - 1);
      break;
    case 'ytd':
      startDate = new Date(now.getFullYear(), 0, 1);
      break;
  }

  return { startDate, endDate };
}

/**
 * Format a date range
 */
export function formatDateRange(startDate: Date, endDate: Date, locale = 'en-US'): string {
  const start = formatDate(startDate, { month: 'short', day: 'numeric' }, locale);
  const end = formatDate(endDate, { month: 'short', day: 'numeric', year: 'numeric' }, locale);
  return `${start} - ${end}`;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
}

/**
 * Format account mask (last 4 digits)
 */
export function formatAccountMask(mask?: string): string {
  if (!mask) return '';
  return `****${mask}`;
}
